import React from 'react'
import { GetToggleButtonPropsOptions } from 'downshift'
import { css } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { DateOption, DateOrNow } from './shared'
import { Placement } from '../popper'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../styled'
import { Dropdown } from '../dropdown'
import { Selection } from '../dropdown/selection'
import { defaultMenuRenderer } from '../dropdown/dropdown'
import { I18nConsumer } from '../../components/i18n-provider'

export const toggleTestID = 'toggle-test-id'

export interface ToggleRendererProps {
  readonly getToggleButtonProps: (options?: GetToggleButtonPropsOptions) => any
  readonly isOpen: boolean
  readonly selectedItem?: DateOption
}

export type ToggleRenderer = (
  props: ToggleRendererProps
) => React.ReactElement<any>

interface DatePickerProps {
  readonly initialDate?: Date
  readonly minDate?: DateOrNow
  readonly maxDate?: DateOrNow
  readonly initialMonth?: DateOrNow

  readonly onCancel: () => void
  readonly onApply: (date: Date) => void
}

const i18nConfig: Record<DateOption, string> = {
  [DateOption.NOW]: 'detail_datePeriodNow',
  [DateOption.THIRTY_MINUTES]: 'detail_datePeriodThirtyMinutes',
  [DateOption.ONE_DAY]: 'detail_datePeriodOneDay',
  [DateOption.ONE_WEEK]: 'detail_datePeriodOneWeek',
  [DateOption.NEVER]: 'title_neverExpires',
  [DateOption.CUSTOM]: 'detail_customOption'
}

interface Props {
  readonly labelID?: string
  readonly markAsRequired?: boolean
  readonly renderToggle?: ToggleRenderer
  readonly dateOptions: DateOption[]
  readonly selectedOption?: DateOption
  readonly selectedDate?: Date
  readonly renderDatePicker: (props: DatePickerProps) => React.ReactElement<any>
  readonly onDateOptionSelect: (option: DateOption) => void
  readonly onCustomDateSelect: (option: Date) => void
  readonly placement?: Placement
  readonly renderAdditionalMenuContent?: () => React.ReactElement<any>
}

interface State {
  readonly isDropdownOpen: boolean
  readonly isCustomPickerOpen: boolean
}

export class DatePickerDropdown extends React.Component<Props, State> {
  public state: State = {
    isDropdownOpen: false,
    isCustomPickerOpen: false
  }

  public render() {
    const {
      labelID,
      markAsRequired,
      selectedOption,
      selectedDate,
      renderDatePicker,
      dateOptions
    } = this.props
    const { isDropdownOpen, isCustomPickerOpen } = this.state

    return (
      <div
        className={css`
          flex: 0 0 auto;
          width: auto;
          line-height: initial;
        `}
      >
        {labelID && (
          <FormattedMessage
            id={labelID}
            children={(label) => (
              <div
                className={css`
                  color: ${theme.color.neutral60};
                  font-weight: ${deprecatedTheme.fontWeights.primary.bold};
                  padding-bottom: 0.25em;
                `}
              >
                {`${label}${markAsRequired ? ' *' : ''}`}
              </div>
            )}
          />
        )}
        <I18nConsumer>
          {({ intl }) => (
            <Dropdown<DateOption>
              onOuterClick={this.onCloseDatePicker}
              onSelect={this.onSelect}
              isOpen={isDropdownOpen || isCustomPickerOpen}
              styleOverrides={{
                outerContainerCss: css`
                  width: unset;
                `,
                selectionContainerCss: css`
                  border-radius: 3px;
                  background-color: white;
                  background-image: unset;
                `
              }}
              items={dateOptions}
              itemToKey={(item) => i18nConfig[item]}
              itemToString={(item) => this.itemToString(intl, item)}
              selectedItem={selectedOption}
              onChange={this.onDateOptionSelect}
              itemSelectionToString={(item) =>
                selectedDate && item === DateOption.CUSTOM
                  ? selectedDate.toLocaleString()
                  : this.itemToString(intl, item)
              }
              selectionToggleRenderer={({
                isOpen,
                selectionItemRenderer,
                itemSelectionToString,
                itemToString,
                styleOverrides,
                getToggleButtonProps,
                selectedItem,
                hideArrow
              }) => {
                const getChildren = () => {
                  if (selectedItem !== undefined) {
                    if (selectionItemRenderer) {
                      return selectionItemRenderer({ item: selectedItem })
                    }

                    if (itemSelectionToString) {
                      return itemSelectionToString(selectedItem)
                    }

                    if (itemToString) {
                      return itemToString(selectedItem)
                    }
                  }

                  return ''
                }
                return (
                  <div data-testid={toggleTestID} onClick={this.toggleDropdown}>
                    <Selection
                      isOpen={isOpen}
                      children={getChildren()}
                      containerCss={
                        styleOverrides && styleOverrides.selectionContainerCss
                      }
                      openContainerCss={
                        styleOverrides &&
                        styleOverrides.selectionOpenContainerCss
                      }
                      textCss={
                        styleOverrides && styleOverrides.selectionTextCss
                      }
                      arrowColor={styleOverrides && styleOverrides.arrowColor}
                      hideArrow={hideArrow}
                      {...getToggleButtonProps()}
                    />
                  </div>
                )
              }}
              menuRenderer={(props) => {
                if (isCustomPickerOpen) {
                  return renderDatePicker({
                    initialDate: selectedDate,
                    onApply: this.onDatePickerApply,
                    onCancel: this.onCloseDatePicker
                  })
                }

                return defaultMenuRenderer(props)
              }}
            />
          )}
        </I18nConsumer>
      </div>
    )
  }

  private toggleDropdown = () => {
    if (this.state.isCustomPickerOpen) {
      this.setState({
        isDropdownOpen: false,
        isCustomPickerOpen: false
      })
    } else {
      this.setState({
        isDropdownOpen: !this.state.isDropdownOpen
      })
    }
  }

  private onSelect = (option: DateOption) => {
    if (option === DateOption.CUSTOM) {
      this.setState({
        isDropdownOpen: true,
        isCustomPickerOpen: true
      })
    } else {
      this.setState({
        isDropdownOpen: !this.state.isDropdownOpen
      })
    }
  }

  private itemToString = (intl, item: DateOption) =>
    intl.formatMessage({ id: i18nConfig[item] })

  private onDateOptionSelect = (option: DateOption) => {
    this.props.onDateOptionSelect(option)
    this.setState({
      isCustomPickerOpen: option === DateOption.CUSTOM,
      isDropdownOpen: false
    })
  }

  private onDatePickerApply = (date: Date) => {
    this.props.onCustomDateSelect(date)
    this.setState({ isDropdownOpen: false, isCustomPickerOpen: false })
  }

  private onCloseDatePicker = () => {
    this.setState({ isDropdownOpen: false, isCustomPickerOpen: false })
  }
}
